import React, { useState, useMemo } from "react";
import useDema from "../../hooks/useDema";
import useCompanyDetails from "../../hooks/useCompanyDetails";
import Loader from "../../components/Loader";
import CompanyList from "../../components/dema/CompanyList";
import CompanyMap from "../../components/dema/CompanyMap";
import CompanyDetails from "../../components/dema/CompanyDetails";
import { useTranslation } from "react-i18next";
import { SimilarCompany } from "../../types/interfaces";

// Función para capitalizar ubicación (ej: "madrid, spain" → "Madrid, Spain")
function buildLocationString(city?: string, country?: string): string {
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  const c = city ? cap(city.trim()) : "";
  const ct = country ? cap(country.trim()) : "";
  return [c, ct].filter(Boolean).join(", ");
}

function mapEmployeeRange(num?: number): string {
  if (!num || num < 0) return "";
  if (num <= 5) return "0-5";
  if (num <= 10) return "6-10";
  if (num <= 20) return "11-20";
  if (num <= 50) return "21-50";
  if (num <= 100) return "51-100";
  if (num <= 500) return "100-500";
  if (num <= 1000) return "500-1000";
  return "1000+";
}

const Dema: React.FC = () => {
  const { t } = useTranslation();

  // Estos Hooks se llaman siempre, sin condiciones
  const { companies, loading, error } = useDema();
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const {
    data: detailsData,
    loading: detailsLoading,
    error: detailsError,
  } = useCompanyDetails(selectedReportId || "");

  const [searchName, setSearchName] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const [employeeRange, setEmployeeRange] = useState("");

  // Procesamos detailsData (si existe) para ordenar por similitud
  const processedCompanies = useMemo<SimilarCompany[]>(() => {
    if (!detailsData) return [];
    const list = [...(detailsData.companies || [])];
    list.sort((a, b) => b.similarity - a.similarity);
    return list;
  }, [detailsData]);

  // Construir opciones de ubicación únicas (capitalizadas)
  const locationOptions = useMemo<string[]>(() => {
    const locSet = new Set<string>();
    for (const comp of processedCompanies) {
      const loc = buildLocationString(comp.city, comp.country);
      if (loc) locSet.add(loc);
    }
    return Array.from(locSet).sort();
  }, [processedCompanies]);

  // Filtrar la lista final de compañías según filtros
  const filteredCompanies = useMemo<SimilarCompany[]>(() => {
    return processedCompanies.filter((comp) => {
      // Filtro por nombre
      if (searchName && !comp.name.toLowerCase().includes(searchName.toLowerCase())) {
        return false;
      }
      // Filtro por ubicación (compara con el string capitalizado)
      if (searchLocation) {
        const loc = buildLocationString(comp.city, comp.country);
        if (loc !== searchLocation) {
          return false;
        }
      }
      // Filtro por rango de empleados
      if (employeeRange) {
        const compRange = mapEmployeeRange(comp.estimated_num_employees);
        if (compRange !== employeeRange) return false;
      }
      return true;
    });
  }, [processedCompanies, searchName, searchLocation, employeeRange]);

  // --- Ahora, después de haber llamado todos los Hooks, podemos hacer early returns
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }

  // Render final
  return (
    <div className="p-8">
      <h1 className="text-4xl font-bold mb-6 text-black">
        {t("dema.pageTitle")}
      </h1>

      {/* LISTADO DE REPORTS (EMPRESAS) */}
      <CompanyList companies={companies} onSelect={setSelectedReportId} />

      {/* MAPA DE EMPRESAS SIMILARES */}
      <h3 className="text-2xl font-bold mt-8 mb-4 text-black">
        {t("dema.mapTitle")}
      </h3>
      <CompanyMap companies={filteredCompanies} />

      {/* DETALLES DE EMPRESAS SIMILARES */}
      {selectedReportId && (
        <div className="mt-8">
          <h2 className="text-3xl font-semibold mb-4 text-black">
            {t("dema.detailsTitle")}
          </h2>
          {detailsLoading ? (
            <Loader />
          ) : detailsError ? (
            <p className="text-red-500">{detailsError}</p>
          ) : detailsData ? (
            <CompanyDetails
              detailsData={{ companies: filteredCompanies, similar_summary: detailsData.similar_summary }}
              searchName={searchName}
              setSearchName={setSearchName}
              searchLocation={searchLocation}
              setSearchLocation={setSearchLocation}
              employeeRange={employeeRange}
              setEmployeeRange={setEmployeeRange}
              locationOptions={locationOptions}
            />
          ) : (
            <p className="text-gray-300">{t("dema.noDetailsFound")}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Dema;
